import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Row, Col } from 'react-bootstrap';
import { withRouter, Route, Switch } from 'react-router-dom';
import { Select, Button, Loader, Modal } from 'sa-ui';
import { pathToRegexp } from 'path-to-regexp/dist';
import NavTabs from 'app/common/NavTabs';

import messages from './reportApplicationDetailsMessages';
import reportMessages from '../reportsMessages';
import * as actions from '../reportsActions';
import * as detailsActions from './reportApplicationDetailsActions';
import * as helpers from '../reportHelpers';
import ResultCard from '../ReportResultCard';
import Chart from '../ReportBarChart';
import RankingStudentCard from '../RankingStudentCard';
import { withForwardRef } from '../../app/hocs';
import { promisify } from '../../app/actions';

import ClassesStudents from './classes-students/ReportApplicationClassesStudentsContainer';
import Components from './components/ReportApplicationComponentsContainer';
import ReportCard from './report-card/ReportCardContainer';
import Abilities from './abilities/ReportApplicationAbilitiesContainer';
import Questions from './questions/ReportApplicationQuestionsContainer';
import AssessmentPanel from './assessment-panel/ReportApplicationAssessmentPanelContainer';
import SchoolCard from '../SchoolCard';

import './ReportApplicationDetailsContainer.scss';

const ReportApplicationDetailsContainer = (props) => {
  const [filter, setFilter] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModalEssay, setShowModalEssay] = useState(false);
  const [hasEssaySSO, setHasEssaySSO] = useState(false);

  function createTabs() {
    const createdTabs = [
      {
        label: props.intl.formatMessage(messages.assessmentPanel),
        path: 'assessment-panel',
        rawComponent: AssessmentPanel,
      },
      {
        label: props.intl.formatMessage(messages.classesStudents),
        path: 'classes-students',
        rawComponent: ClassesStudents,
      },
      {
        label: props.intl.formatMessage(messages.questions),
        path: 'questions',
        rawComponent: Questions,
      },
      {
        label: props.intl.formatMessage(messages.components),
        path: 'components',
        rawComponent: Components,
      },
      {
        label: props.intl.formatMessage(messages.abilities),
        path: 'abilities',
        rawComponent: Abilities,
      },
      {
        label: props.intl.formatMessage(messages.reportCard),
        path: 'report-card',
        rawComponent: ReportCard,
      },
    ];
    return createdTabs.map((tab) => ({
      ...tab,
      ref: React.createRef(),
      active: pathToRegexp(`/reports/application/:idEvaluation/${tab.path}`).test(
        window.location.pathname,
      ),
      component: withForwardRef(tab.rawComponent),
    }));
  }
  const tabs = useMemo(createTabs, [window.location.pathname]);

  function goBack() {
    props.history.goBack();
  }

  function handleClickTab(tab) {
    props.history.push(`/reports/application/${props.idEvaluation}/${tab.path}`);
  }

  function getCurrentTab() {
    return tabs.find((tab) =>
      pathToRegexp(`/reports/application/:idEvaluation/${tab.path}`).test(window.location.pathname),
    );
  }
  async function generatePdf() {
    const currentTab = getCurrentTab();
    if (!currentTab || !currentTab.ref.current || !currentTab.ref.current.getFilter) {
      console.error('No supported tab with filter function identified.');
      return;
    }
    setLoading(true);
    try {
      await props.generatePdf({
        filename: `download.pdf`,
        host: `${window.location.protocol}//${window.location.host}`,
        path: window.location.pathname,
        params: { ...currentTab.ref.current.getFilter(), rankingLimit: 100, expanded: true },
        token: props.token,
      });
    } finally {
      setLoading(false);
    }
  }

  function handleEssaySSO() {
    setShowModalEssay(true);
  }

  function handleCloseModalEssay() {
    setShowModalEssay(false);
    props.getEssaySsoUrl();
  }

  useEffect(() => {
    if (!props.essaySSOUrlFetched && !props.loadingEssaySSO) {
      props.getEssaySsoUrl();
    }
  }, []);

  useEffect(() => {
    if (props.essaySSOUrl) {
      setHasEssaySSO(true);
    }
  }, [props.essaySSOUrl]);

  return (
    <div className="report-general-panel">
      <Loader show={loading} />
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <Row className="align-items-end d-print-none">
            <Col md={12}>
              <a className="return" onClick={goBack}>
                <i className="ti-arrow-left" />
                {props.intl.formatMessage(reportMessages.back)}
              </a>
              <h5 className="title">{props.intl.formatMessage(messages.title)}</h5>
            </Col>
          </Row>
          {props.evaluation &&
            props.evaluation.evaluation &&
            props.evaluation.evaluation.levelSegment && (
              <h6 className="subtitle">
                <span>
                  {props.evaluation.evaluation.catalogings.map((c) => c.name).join(' | ')}{' '}
                </span>
                <span>ID: {props.evaluation.evaluation.idString}</span>
              </h6>
            )}
        </div>
        {props.user.hasEvaluationWithEssay && hasEssaySSO && props.user.savPermission == 0 && (
          <Button primary style={{ height: '70px', marginRight: '20px' }} onClick={handleEssaySSO}>
            {props.intl.formatMessage(messages.accessEssayCorrections)}
          </Button>
        )}
      </div>
      <hr />
      {props.essaySSOUrlFetched ? (
        <div
        // style={{
        //   display:
        //     props.user.hasEvaluationWithEssay && props.user.savPermission > 0 ? 'block' : 'none',
        // }}
        >
          <Row className="d-print-none">
            <Col md={12} className="d-flex justify-content-end mb-3">
              {hasEssaySSO && (
                <Button
                  primary
                  style={{ height: '70px', marginRight: '20px' }}
                  onClick={handleEssaySSO}
                >
                  {props.intl.formatMessage(messages.accessEssayCorrections)}
                </Button>
              )}
              <Button primary style={{ height: 70 }} onClick={generatePdf}>
                {props.intl.formatMessage(reportMessages.generatePdf)}
              </Button>
            </Col>
          </Row>
          <NavTabs tabs={tabs} onClick={handleClickTab} />
          {tabs.map((tab) => (
            <Route
              key={tab.path}
              path={`/reports/application/:idEvaluation/${tab.path}`}
              render={(innerProps) => <tab.component {...tab} {...innerProps} />}
            />
          ))}
        </div>
      ) : (
        <Loader show={true} />
      )}

      <Modal show={showModalEssay} onClose={() => handleCloseModalEssay()} large>
        <iframe src={props.essaySSOUrl} frameborder="0"></iframe>
      </Modal>
    </div>
  );
};

ReportApplicationDetailsContainer.propTypes = {};

const mapStateToProps = ({ reports, reportApplicationDetails, app }, { match }) => ({
  ...match.params,
  ...reports,
  ...reportApplicationDetails,
  token: app.cookieToken,
  permission: app.permission,
  user: app.user,
});

export default compose(
  injectIntl,
  withRouter,
  connect(
    mapStateToProps,
    promisify({ ...actions, ...detailsActions }, { include: ['generatePdf'] }),
  ),
)(ReportApplicationDetailsContainer);
